/**
 * custom-header.js — Moncler-style scroll behavior for Maskaľ.sk
 *
 * Strategy:
 *
 *  1. Neutralize the theme's StickyHeader.prototype.onScrollHandler so it
 *     stops writing inline transform / CSS variables that would otherwise
 *     beat our state-class CSS rules.
 *
 *  2. Toggle `is-hidden` and `is-solid` on `.header-section` based on scroll
 *     direction. Triggers immediately on any scroll past 1px (matches
 *     Moncler — header starts sliding the moment you start scrolling, no
 *     "wait for header height" threshold).
 *
 *  3. The companion CSS rules in theme.css ensure `is-hidden` keeps logo
 *     opacity at 1 (dark logo). This prevents the brief white-logo flash
 *     that otherwise happens during the moment the header is in motion
 *     (when only `header-scrolled` is set, before `is-hidden`/`is-solid`
 *     classes activate).
 *
 *  4. Re-sync state on `load` and `pageshow` events to handle browser
 *     scroll restoration on reload. Without this, reloading mid-page
 *     leaves the header in its default transparent state (white logo on
 *     light background = unreadable) because window.scrollY is still 0
 *     during DOMContentLoaded — the browser restores scroll position
 *     afterwards, without firing a scroll event.
 *
 * State map (on .header-section):
 *   - top of page (scrollY === 0)  → no class    → default transparent
 *   - any scroll DOWN past 1px     → `is-hidden` → slide off-screen
 *   - any scroll UP                → `is-solid`  → slide back, solid white
 *   - reload mid-page              → `is-solid`  → same as scrolled-up state
 */
(function () {
  "use strict";

  function neutralizeStickyHeader() {
    var Ctor = customElements.get && customElements.get("sticky-header");
    if (!Ctor || !Ctor.prototype) return false;
    if (Ctor.prototype.__neutralized) return true;

    Ctor.prototype.__origOnScrollHandler = Ctor.prototype.onScrollHandler;
    Ctor.prototype.onScrollHandler = function () {
      /* neutralized — handled by custom-header.js state classes instead */
    };
    Ctor.prototype.__neutralized = true;

    var header = document.querySelector("header[is=sticky-header]");
    if (header) {
      header.style.transform = "";
      header.style.removeProperty("--header-background-opacity");
      header.style.removeProperty("--header-logo-opacity");
      if (!header.getAttribute("style")) header.removeAttribute("style");
    }
    return true;
  }

  function ensureNeutralized(callback) {
    if (neutralizeStickyHeader()) {
      callback();
      return;
    }
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (neutralizeStickyHeader() || tries > 50) {
        clearInterval(iv);
        callback();
      }
    }, 20);
  }

  function init() {
    var header = document.querySelector("header[is=sticky-header]") ||
                 document.querySelector(".header");
    var headerSection = document.querySelector(".header-section");
    if (!header || !headerSection) return;

    // Trigger threshold: 1px. The header should start sliding the instant
    // the user scrolls, not after they've passed the header's own height.
    var HIDE_THRESHOLD = 1;

    var lastScrollY = window.scrollY;
    var ticking = false;

    function update() {
      var y = Math.max(0, window.scrollY);
      var delta = y - lastScrollY;

      if (y === 0) {
        headerSection.classList.remove("is-hidden");
        headerSection.classList.remove("is-solid");
      } else if (delta > 0 && y > HIDE_THRESHOLD) {
        headerSection.classList.add("is-hidden");
        headerSection.classList.remove("is-solid");
      } else if (delta < 0) {
        headerSection.classList.remove("is-hidden");
        headerSection.classList.add("is-solid");
      }

      lastScrollY = y;
      ticking = false;
    }

    // Sync header state to the current absolute scroll position, with no
    // delta logic. Used on initial load and after browser scroll
    // restoration (load / pageshow events) to put the header in the
    // correct state without animating from a stale lastScrollY value.
    function syncToCurrentScroll() {
      var y = Math.max(0, window.scrollY);
      lastScrollY = y;
      if (y === 0) {
        headerSection.classList.remove("is-hidden");
        headerSection.classList.remove("is-solid");
      } else {
        // Restored mid-page → treat as if user had scrolled up to here.
        headerSection.classList.remove("is-hidden");
        headerSection.classList.add("is-solid");
      }
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });

    // Initial sync — covers normal page load at scrollY = 0.
    syncToCurrentScroll();

    // Re-sync after browser restores scroll position on reload.
    // 'load' fires after scroll restoration is complete; 'pageshow'
    // additionally covers back/forward cache (bfcache) restores.
    window.addEventListener("load", syncToCurrentScroll);
    window.addEventListener("pageshow", syncToCurrentScroll);
  }

  function start() {
    ensureNeutralized(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();